/**
 * Custom hook for meetings operations
 */

import {useEffect} from 'react';
import {useMeetingsStore} from '@voxapilot/shared';
import {api} from '../config/firebase';
import {useUIStore} from '@voxapilot/shared';

export function useMeetings() {
  const {
    meetings,
    filters,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    setMeetings,
    updateMeeting,
    removeMeeting,
    setFilters,
    setLoading,
    setCreating,
    setUpdating,
    setDeleting,
    setError,
    clearError,
  } = useMeetingsStore();

  const {addToast} = useUIStore();

  // Load meetings when filters change
  useEffect(() => {
    loadMeetings();
  }, [filters.limit, filters.offset, filters.status, filters.startDate, filters.endDate, filters.search]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      clearError();

      const response = await api.meetings.listMeetings(filters);
      if (response.success && response.data) {
        setMeetings(
          response.data.items,
          response.data.total,
          response.data.hasMore
        );
      } else {
        throw new Error(response.error?.message || 'Failed to load meetings');
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const createMeeting = async (data: Parameters<typeof api.meetings.createMeeting>[0]) => {
    try {
      setCreating(true);
      clearError();

      const response = await api.meetings.createMeeting(data);
      if (response.success && response.data) {
        // Reload meetings to get the new one with full data
        await loadMeetings();
        addToast('Meeting created successfully', 'success');
        return response.data.meetingId;
      } else {
        throw new Error(response.error?.message || 'Failed to create meeting');
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      addToast(error.message, 'error');
      throw error;
    } finally {
      setCreating(false);
    }
  };

  const updateMeetingById = async (
    meetingId: string,
    data: Parameters<typeof api.meetings.updateMeeting>[1]
  ) => {
    try {
      setUpdating(true);
      clearError();

      const response = await api.meetings.updateMeeting(meetingId, data);
      if (response.success && response.data) {
        updateMeeting(meetingId, response.data);
        addToast('Meeting updated successfully', 'success');
      } else {
        throw new Error(response.error?.message || 'Failed to update meeting');
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      addToast(error.message, 'error');
      throw error;
    } finally {
      setUpdating(false);
    }
  };

  const deleteMeetingById = async (meetingId: string) => {
    try {
      setDeleting(true);
      clearError();

      const response = await api.meetings.deleteMeeting(meetingId);
      if (response.success) {
        removeMeeting(meetingId);
        addToast('Meeting deleted successfully', 'success');
      } else {
        throw new Error(response.error?.message || 'Failed to delete meeting');
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      addToast(error.message, 'error');
      throw error;
    } finally {
      setDeleting(false);
    }
  };

  return {
    meetings,
    filters,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    loadMeetings,
    createMeeting,
    updateMeeting: updateMeetingById,
    deleteMeeting: deleteMeetingById,
    setFilters,
    clearError,
  };
}

